import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ChevronLeft, Sparkles, Baby, Users, User, UserCheck, ArrowRight, ShoppingCart, Download, FileText, Package as PackIcon, Check, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

// Icônes par slug âge (le backend n'envoie que slug/label/sub)
const AGE_ICONS = { bebe: Baby, enfant: User, ado: Users, adulte: UserCheck, senior: UserCheck };

// ─── Résultat card ────────────────────────────────────────────────────
function ProductResult({ item, kind, onAdd }) {
  const { fmt, lang } = useApp() || {};
  const isRes = kind === "resource";
  const name = isRes ? item.titre : (typeof item.name === "object" ? (item.name[lang] || item.name.fr) : item.name);
  const img = isRes ? (item.cover || item.apercu) : (item.images && item.images[0]);
  const price = isRes ? item.prix : (item.sale_price || item.price);
  const free = isRes && item.isGratuit;
  const to = isRes ? `/ressources-a-telecharger/${item.slug}` : `/produit/${item.slug}`;

  return (
    <div className="flex gap-3 bg-white border border-ink/10 rounded-2xl p-3 hover:shadow-md transition group" data-testid={`sa-result-${kind}-${item.slug}`}>
      <Link to={to} className="shrink-0 w-20 h-24 rounded-xl overflow-hidden bg-ivory-200">
        {img ? (
          <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink/30">
            {isRes ? <FileText size={22} /> : <PackIcon size={22} />}
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0 flex flex-col">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-pedablue-dark mb-1">
          {isRes ? <FileText size={10} /> : <PackIcon size={10} />}
          {isRes ? "Ressource PDF" : "Matériel physique"}
        </span>
        <Link to={to} className="text-sm font-medium leading-tight line-clamp-2 hover:text-pedablue mb-1">
          {name}
        </Link>
        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="text-sm font-display">
            {free ? <span className="text-sage-dark">Gratuit</span> : (fmt ? fmt(price) : `${(price || 0).toFixed(2)} €`)}
          </span>
          <button
            onClick={() => onAdd(item, kind)}
            className="inline-flex items-center gap-1 rounded-full bg-pedablue text-ivory text-[11px] px-2.5 py-1.5 hover:bg-pedablue-dark whitespace-nowrap"
            data-testid={`sa-add-${kind}-${item.slug}`}
            title="Ajouter au panier"
          >
            {isRes ? <Download size={11} /> : <ShoppingCart size={11} />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Étape "chip picker" générique ────────────────────────────────────
function StepPicker({ options, onPick, testidPrefix, columns = 2 }) {
  return (
    <div className={`grid grid-cols-1 ${columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-2.5`}>
      {options.map((opt) => {
        const Icon = opt.Icon;
        return (
          <button
            key={opt.slug || opt}
            onClick={() => onPick(opt.slug || opt, opt.label || opt)}
            data-testid={`${testidPrefix}-${opt.slug || opt}`}
            className="group text-left bg-white border border-ink/10 rounded-2xl p-3.5 hover:border-pedablue hover:shadow-md hover:-translate-y-0.5 transition-all"
          >
            <div className="flex items-center gap-2.5">
              {Icon && (
                <span className="shrink-0 w-9 h-9 rounded-full bg-pedablue/10 text-pedablue-dark flex items-center justify-center group-hover:bg-pedablue group-hover:text-ivory transition">
                  <Icon size={16} />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-tight group-hover:text-pedablue">{opt.label || opt}</p>
                {opt.sub && <p className="text-[11px] text-ink-soft mt-0.5">{opt.sub}</p>}
              </div>
              <ArrowRight size={14} className="text-ink/30 group-hover:text-pedablue group-hover:translate-x-0.5 transition" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────
export default function SessionAssistant({ onClose }) {
  const { addToCart } = useApp() || {};
  const [step, setStep] = useState(1);
  const [domaine, setDomaine] = useState(null);
  const [age, setAge] = useState(null);
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  // ─── Facettes dynamiques (ne montre que ce qui est peuplé en BDD)
  const [domainesFacet, setDomainesFacet] = useState(null);   // null = loading
  const [agesFacet, setAgesFacet] = useState(null);
  const [profilsFacet, setProfilsFacet] = useState(null);

  // Étape 1 : fetch domaines dispos au mount
  useEffect(() => {
    api.get("/session-assistant/facets")
      .then((r) => setDomainesFacet(r.data.options || []))
      .catch(() => setDomainesFacet([]));
  }, []);

  // Étape 2 : fetch ages dispos quand un domaine est choisi
  useEffect(() => {
    if (!domaine) { setAgesFacet(null); return; }
    setAgesFacet(null);
    api.get(`/session-assistant/facets?domaine=${encodeURIComponent(domaine.slug)}`)
      .then((r) => setAgesFacet((r.data.options || []).map((a) => ({ ...a, Icon: AGE_ICONS[a.slug] || User }))))
      .catch(() => setAgesFacet([]));
  }, [domaine]);

  // Étape 3 : fetch profils dispos quand (domaine, age) sont choisis
  useEffect(() => {
    if (!domaine || !age) { setProfilsFacet(null); return; }
    setProfilsFacet(null);
    api.get(`/session-assistant/facets?domaine=${encodeURIComponent(domaine.slug)}&age_bucket=${encodeURIComponent(age.slug)}`)
      .then((r) => setProfilsFacet(r.data.options || []))
      .catch(() => setProfilsFacet([]));
  }, [domaine, age]);

  async function fetchResults(dom, ageBucket, prof) {
    setLoading(true);
    try {
      const r = await api.post("/session-assistant/recommend", {
        domaine: dom.slug,
        age_bucket: ageBucket.slug,
        profil: prof,
      });
      setResults(r.data);
    } catch (e) {
      toast.error("Impossible de charger les suggestions");
      setResults({ resources: [], products: [], context: {} });
    } finally { setLoading(false); }
  }

  function pickDomaine(_slug, _label) {
    const d = (domainesFacet || []).find((x) => x.slug === _slug);
    setDomaine(d);
    setStep(2);
  }
  function pickAge(_slug) {
    const a = (agesFacet || []).find((x) => x.slug === _slug);
    setAge(a);
    setStep(3);
  }
  function pickProfil(p) {
    setProfil(p);
    setStep(4);
    fetchResults(domaine, age, p);
  }

  function restart() {
    setStep(1); setDomaine(null); setAge(null); setProfil(null); setResults(null);
  }

  function handleAdd(item, kind) {
    if (!addToCart) return;
    const isRes = kind === "resource";
    const name = isRes ? item.titre : (typeof item.name === "object" ? (item.name.fr) : item.name);
    const price = isRes ? item.prix : (item.sale_price || item.price);
    const img = isRes ? (item.cover || item.apercu) : (item.images && item.images[0]);
    addToCart({
      id: `${isRes ? "resource" : item.id}-${item.id}`,
      slug: isRes ? `resource-${item.id}` : item.slug,
      name: { fr: name },
      price,
      images: img ? [img] : [],
    }, 1);
    toast.success(`« ${name.slice(0, 40)} » ajouté au panier`);
  }

  const totalSteps = 3;
  const progress = Math.min(step, totalSteps + 1);

  return (
    <div className="fixed inset-0 z-[110] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto" data-testid="session-assistant-modal">
      <div className="bg-ivory rounded-3xl w-full max-w-2xl shadow-2xl relative my-8" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-ink/5 hover:bg-ink/10 flex items-center justify-center transition z-10" aria-label="Fermer" data-testid="sa-close">
          <X size={16} />
        </button>

        {/* Progress bar */}
        <div className="px-6 sm:px-8 pt-6">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${progress > i ? "bg-pedablue" : progress === i ? "bg-pedablue/60" : "bg-ink/10"}`} />
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] uppercase tracking-widest text-ink-soft">
              {step <= 3 ? `Étape ${step}/3` : "Recommandations"}
            </p>
            {step > 1 && step <= 3 && (
              <button onClick={() => setStep(step - 1)} className="text-[11px] text-ink-soft hover:text-ink inline-flex items-center gap-1" data-testid="sa-back">
                <ChevronLeft size={12} /> Retour
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 sm:px-8 py-6">
          {step === 1 && (
            <>
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-widest text-pedablue-dark flex items-center gap-1.5 mb-2">
                  <Sparkles size={12} /> Assistant de séance
                </p>
                <h2 className="font-display text-2xl sm:text-3xl leading-tight">Que voulez-vous travailler&nbsp;?</h2>
                <p className="text-sm text-ink-soft mt-2">Choisissez un domaine clinique pour démarrer.</p>
              </div>
              {domainesFacet === null ? (
                <div className="text-center py-8 text-sm text-ink-soft">Chargement…</div>
              ) : domainesFacet.length === 0 ? (
                <div className="text-center py-8 text-sm text-ink-soft">Aucun domaine avec des ressources disponibles pour le moment.</div>
              ) : (
                <StepPicker options={domainesFacet} onPick={pickDomaine} testidPrefix="sa-domaine" columns={2} />
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-widest text-pedablue-dark mb-2">
                  <Check className="inline mr-1" size={11} /> {domaine?.label}
                </p>
                <h2 className="font-display text-2xl sm:text-3xl leading-tight">Quel est l&apos;âge du patient&nbsp;?</h2>
              </div>
              {agesFacet === null ? (
                <div className="text-center py-8 text-sm text-ink-soft">Chargement…</div>
              ) : agesFacet.length === 0 ? (
                <div className="text-center py-8 text-sm text-ink-soft">Aucune tranche d'âge n'a de ressource pour ce domaine actuellement.</div>
              ) : (
                <StepPicker options={agesFacet} onPick={pickAge} testidPrefix="sa-age" columns={3} />
              )}
            </>
          )}

          {step === 3 && (
            <>
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-widest text-pedablue-dark mb-2">
                  <Check className="inline mr-1" size={11} /> {domaine?.label} · {age?.label}
                </p>
                <h2 className="font-display text-2xl sm:text-3xl leading-tight">Profil spécifique&nbsp;?</h2>
                <p className="text-sm text-ink-soft mt-2">Sélectionnez le trouble le plus proche de votre patient.</p>
              </div>
              {profilsFacet === null ? (
                <div className="text-center py-8 text-sm text-ink-soft">Chargement…</div>
              ) : profilsFacet.length === 0 ? (
                <div className="text-center py-8 text-sm text-ink-soft">
                  Aucun profil clinique n'a de ressource pour ce couple actuellement.<br/>
                  <button onClick={restart} className="text-pedablue-dark underline mt-2 text-xs">Recommencer</button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {profilsFacet.map((p) => (
                    <button
                      key={p}
                      onClick={() => pickProfil(p)}
                      data-testid={`sa-profil-${p.replace(/[^a-z]/gi, "").toLowerCase()}`}
                      className="text-left bg-white border border-ink/10 rounded-2xl p-3 hover:border-pedablue hover:shadow-md transition-all text-sm"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sage-dark mb-1 flex items-center gap-1"><Check size={11} /> Séance préparée</p>
                  <h2 className="font-display text-2xl sm:text-3xl leading-tight">Vos 4 outils recommandés</h2>
                  <p className="text-xs text-ink-soft mt-1">
                    {domaine?.label} · {age?.label} · {profil}
                  </p>
                </div>
                <button onClick={restart} className="text-[11px] text-ink-soft hover:text-ink inline-flex items-center gap-1 shrink-0 mt-1" data-testid="sa-restart">
                  <RefreshCw size={11} /> Recommencer
                </button>
              </div>

              {loading ? (
                <div className="py-12 text-center text-sm text-ink-soft">
                  <RefreshCw size={24} className="mx-auto mb-3 animate-spin text-pedablue" />
                  Analyse de votre patient en cours…
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">📥 Ressources PDF</p>
                    <div className="space-y-2">
                      {(results?.resources || []).slice(0, 2).map((r) => (
                        <ProductResult key={r.id} item={r} kind="resource" onAdd={handleAdd} />
                      ))}
                      {(results?.resources || []).length === 0 && (
                        <p className="text-xs text-ink-soft italic">Aucune ressource digitale ne correspond exactement — parcourez le <Link to="/ressources-a-telecharger" onClick={onClose} className="underline">catalogue complet</Link>.</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">📦 Matériel physique</p>
                    <div className="space-y-2">
                      {(results?.products || []).slice(0, 2).map((p) => (
                        <ProductResult key={p.id} item={p} kind="product" onAdd={handleAdd} />
                      ))}
                      {(results?.products || []).length === 0 && (
                        <p className="text-xs text-ink-soft italic">Aucun matériel correspondant — voir <Link to="/catalogue" onClick={onClose} className="underline">tout le matériel</Link>.</p>
                      )}
                    </div>
                  </div>
                  <div className="pt-3 border-t border-ink/10 flex flex-wrap gap-2 justify-between items-center">
                    <p className="text-[11px] text-ink-soft italic">Sélection Orthophonie Online · Créée par des orthophonistes.</p>
                    <Link to="/panier" onClick={onClose} className="btn-primary text-xs !py-2 inline-flex items-center gap-1.5" data-testid="sa-goto-cart">
                      <ShoppingCart size={12} /> Voir mon panier
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
